import React, { useEffect, useState } from "react";
import { showError, showSuccess } from "../../utils/toastUtils";
import addressAPI from "../../api/addressApi";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";
import { FiMapPin, FiPlus, FiMinus, FiTrash2, FiUser, FiHome, FiBriefcase, FiGlobe } from "react-icons/fi";

function Addresses() {
  useAlphabeticInput();
  useNumericInput();

  const [isOpen, setIsOpen] = useState(false);
  const toggleAccordion = () => setIsOpen(!isOpen);
  const [addresses, setAddresses] = useState([]);

  const INITAL_FORM_DATA = {
    firstName: "",
    lastName: "",
    addressType: "home",
    addressLine1: "",
    addressLine2: "",
    pinCode: "",
    district: "",
    state: "",
    country: "",
  };

  const [formData, setFormData] = useState(INITAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAddress();
  }, []);

  const fetchAddress = async () => {
    try {
      const res = await addressAPI.getAllAddress();
      if (res.code === 200) {
        setAddresses(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = "Please Enter First Name";
    if (!formData.addressType) newErrors.addressType = "Please Enter Address Type";
    if (!formData.addressLine1) newErrors.addressLine1 = "Please Enter Address";
    
    if (!formData.pinCode) {
      newErrors.pinCode = "Please Enter PinCode";
    } else if (formData.pinCode.length !== 6) {
      newErrors.pinCode = "PinCode must be of 6 digits";
    }

    if (!formData.district) newErrors.district = "Please Enter District";
    if (!formData.state) newErrors.state = "Please Enter State";
    if (!formData.country) newErrors.country = "Please Enter Country";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const res = await addressAPI.addNewAddress(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setTimeout(function () {
          setIsOpen(false);
          fetchAddress();
        }, 1000);
        setFormData(INITAL_FORM_DATA);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const deleteAddress = async (id) => {
    try {
      const res = await addressAPI.deleteAddress(id);
      if (res.code === 200) {
        fetchAddress();
      }
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <>
      <div className="account-address-funky mb-5">
        {/* Title Sticker Banner */}
        <div className="address-dashboard-header mb-4">
          <span className="badge-text-purple">MY ADDRESSES ({addresses.length})</span>
        </div>

        {/* Form Accordion Container */}
        <div className="funky-accordion-wrapper mb-5">
          <div
            className={`funky-accordion-title ${isOpen ? "active" : ""}`}
            onClick={toggleAccordion}
            style={{ cursor: "pointer" }}
          >
            <span className="d-flex align-items-center gap-2">
              {isOpen ? <FiMinus /> : <FiPlus />} Add New Address
            </span>
          </div>

          {isOpen && (
            <div className="funky-accordion-body p-4">
              <form onSubmit={handleSubmit}>
                {/* CONTACT DETAILS */}
                <h5 className="funky-form-subheading mb-3"><FiUser className="me-2"/> Contact Person Details</h5>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="funky-dash-label">First Name</label>
                    <input
                      className="funky-dash-input only-alphabets"
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="First Name"
                    />
                    {errors.firstName && <small className="text-danger d-block mt-1 fw-bold">{errors.firstName}</small>}
                  </div>
                  <div className="col-md-6">
                    <label className="funky-dash-label">Last Name</label>
                    <input
                      className="funky-dash-input only-alphabets"
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Last Name"
                    />
                  </div>
                </div>

                {/* ADDRESS DETAILS */}
                <h5 className="funky-form-subheading mb-3"><FiMapPin className="me-2"/> Address Details</h5>
                
                {/* Radio Selector Tabs */}
                <div className="d-flex gap-3 mb-4 flex-wrap">
                  <label className={`funky-radio-tab ${formData.addressType === "home" ? "active" : ""}`}>
                    <input type="radio" name="addressType" value="home" checked={formData.addressType === "home"} onChange={handleChange} className="d-none" />
                    <FiHome /> Home
                  </label>
                  <label className={`funky-radio-tab ${formData.addressType === "work" ? "active" : ""}`}>
                    <input type="radio" name="addressType" value="work" checked={formData.addressType === "work"} onChange={handleChange} className="d-none" />
                    <FiBriefcase /> Office
                  </label>
                  <label className={`funky-radio-tab ${formData.addressType === "other" ? "active" : ""}`}>
                    <input type="radio" name="addressType" value="other" checked={formData.addressType === "other"} onChange={handleChange} className="d-none" />
                    <FiMapPin /> Other
                  </label>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <label className="funky-dash-label">Address Line 1</label>
                    <input className="funky-dash-input" type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder="Flat, House no., Building, Company" />
                    {errors.addressLine1 && <small className="text-danger d-block mt-1 fw-bold">{errors.addressLine1}</small>}
                  </div>
                  <div className="col-12">
                    <label className="funky-dash-label">Address Line 2 (Optional)</label>
                    <input className="funky-dash-input" type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder="Area, Street, Sector, Village" />
                  </div>
                  <div className="col-md-4">
                    <label className="funky-dash-label">Pin Code</label>
                    <input className="funky-dash-input only-numeric" type="text" maxLength={6} name="pinCode" value={formData.pinCode} onChange={handleChange} placeholder="6 Digits" />
                    {errors.pinCode && <small className="text-danger d-block mt-1 fw-bold">{errors.pinCode}</small>}
                  </div>
                  <div className="col-md-4">
                    <label className="funky-dash-label">District</label>
                    <input className="funky-dash-input only-alphabets" type="text" name="district" value={formData.district} onChange={handleChange} placeholder="District" />
                    {errors.district && <small className="text-danger d-block mt-1 fw-bold">{errors.district}</small>}
                  </div>
                  <div className="col-md-4">
                    <label className="funky-dash-label">State</label>
                    <input className="funky-dash-input only-alphabets" type="text" name="state" value={formData.state} onChange={handleChange} placeholder="State" />
                    {errors.state && <small className="text-danger d-block mt-1 fw-bold">{errors.state}</small>}
                  </div>
                  <div className="col-12">
                    <label className="funky-dash-label">Country</label>
                    <input className="funky-dash-input only-alphabets" type="text" name="country" value={formData.country} onChange={handleChange} placeholder="Country" />
                    {errors.country && <small className="text-danger d-block mt-1 fw-bold">{errors.country}</small>}
                  </div>
                </div>

                <button className="funky-address-submit-btn" type="submit">
                  SAVE ADDRESS ⚡
                </button>
              </form>
            </div>
          )}
        </div>

        {/* SAVED ADDRESSES GRID */}
        {addresses && addresses.length > 0 && (
          <div className="row g-4">
            {addresses.map((address) => (
              <div className="col-md-6" key={address._id}>
                <div className="saved-address-card-funky">
                  <div className="card-top-bar">
                    <span className="address-type-tag">{address.addressType.toUpperCase()}</span>
                    <button className="delete-btn-funky" onClick={() => deleteAddress(address._id)} aria-label="Delete Address">
                      <FiTrash2 />
                    </button>
                  </div>
                  <div className="card-body-content">
                    <h6 className="person-name">{address.firstName} {address.lastName}</h6>
                    <p className="full-address-text">
                      {address.addressLine1}{address.addressLine2 ? `, ${address.addressLine2}` : ""}, <br />
                      {address.district}, {address.state} - <strong>{address.pinCode}</strong> <br />
                      <span className="country-text"><FiGlobe className="me-1" />{address.country}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default Addresses;